import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import type { Group, IssueTreeGroup } from "@/lib/issues";
import { liveIssues, patchIssue, type ProjectData } from "@/lib/store";
import { TreeDropRoot, TreeDropRow, useTreeDrag } from "./TreeDrag";
import type { ViewSettings } from "@/lib/views";
import { IssueRow } from "./IssueRow";
import { StateIcon } from "./PropertyIcons";

export type RowPickers = { issueId: string; open: "state" | "priority" | "assignee" | "label" } | null;

export function IssueList({
  d,
  groups,
  view,
  focusedId,
  selected,
  onFocus,
  onToggleSelect,
  rowPickers,
  onClosePicker,
  onCreateInGroup,
  collapsed,
  onToggle,
  onExpand,
}: {
  d: ProjectData;
  groups: IssueTreeGroup[];
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  onExpand: (id: string) => void;
  view: ViewSettings;
  focusedId: string | null;
  selected: Set<string>;
  onFocus: (id: string) => void;
  onToggleSelect: (id: string) => void;
  rowPickers: RowPickers;
  onClosePicker: () => void;
  onCreateInGroup?: (g: Group) => void;
}) {
  const { t } = useTranslation();
  const membership = new Map<string, Set<string>>();
  for (const group of groups) for (const issue of group.issues) {
    const keys = membership.get(issue.id) ?? new Set<string>(); keys.add(group.key); membership.set(issue.id, keys);
  }
  const drag = useTreeDrag({ items: liveIssues(d), disabled: !d.project.role || view.orderBy !== "manual", onExpand,
    onMove: (id, move) => patchIssue(d.issues[id]!, move),
    canDrop: (source, target, position) => !target || position === "inside" || view.groupBy === "none"
      || [...(membership.get(source) ?? [])].some((key) => membership.get(target)?.has(key)),
  });
  return (
    <div data-tree-scroll className={`min-h-0 flex-1 overflow-y-auto ${selected.size > 0 ? "pb-28" : "pb-6"}`}>
      {groups.map((g) => (
        <section key={g.key}>
          {view.groupBy !== "none" && (
            <header className="group/h sticky top-0 z-10 flex h-10 items-center gap-2.5 border-b border-border bg-sidebar/95 px-4 text-[12px] backdrop-blur">
              {g.state ? <StateIcon state={g.state} /> : g.color ? <span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color }} /> : null}
              <span className="min-w-0 truncate font-medium" title={g.label}>{g.label}</span>
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md bg-hover px-1 text-[11px] tabular-nums text-muted">{g.issues.length}</span>
              {onCreateInGroup && (
                <button className="ml-auto shrink-0 rounded-md p-1.5 text-muted transition-colors hover:bg-hover hover:text-fg" title={t("nav.newIssue")} aria-label={t("nav.newIssue")} onClick={() => onCreateInGroup(g)}>
                  <Plus className="h-3.5 w-3.5" />
                </button>
              )}
            </header>
          )}
          {g.rows.map(({ item: i, depth, children }) => (
            <TreeDropRow key={`${g.key}:${i.id}`} tree={drag} id={i.id} depth={depth}><IssueRow
              depth={depth}
              treeControls={<>{drag.handle(i, i.title)}<button type="button" aria-label={collapsed.has(i.id) ? t("tree.expand") : t("tree.collapse")} className={`flex h-6 w-4 shrink-0 items-center justify-center text-faint hover:text-fg ${children.length ? "" : "invisible"}`} onClick={() => onToggle(i.id)}>
                {collapsed.has(i.id) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button></>}
              d={d}
              issue={i}
              view={view}
              focused={focusedId === i.id}
              selected={selected.has(i.id)}
              onFocus={() => onFocus(i.id)}
              onToggleSelect={() => onToggleSelect(i.id)}
              pickers={{ open: rowPickers?.issueId === i.id ? rowPickers.open : null, onClose: onClosePicker }}
            /></TreeDropRow>
          ))}
        </section>
      ))}
      <TreeDropRoot tree={drag} />
    </div>
  );
}
